/**
 * useBillState — the one answer to "what does this persona owe right now?".
 *
 * Every surface that renders a balance, a due date, a paid marker, or a payment
 * cap derives it from here, so web and mobile cannot tell two different stories
 * about the same persona on the same day: the cross-surface incoherence becomes
 * impossible by construction rather than by discipline.
 *
 * Two rules it enforces that the calendar-phased predecessor could not:
 *
 *  1. **Settled-ness is user-action-driven.** A bill reads paid only when this
 *     session's payments cover it — never because of what day it is. The old
 *     `billWindow()` flipped every persona to "Paid ✓" from the 23rd with no
 *     transaction behind it, which both fabricated a state and made the pay
 *     journey undemonstrable for ~28% of every month.
 *  2. **The paid date is the date the customer actually paid**, read off the
 *     ledger entry, not the bill's due date.
 *
 * Split pure-core / React-seam on the `resolveHomeAlerts` precedent: the
 * arithmetic lives in `resolveBillState` so it is directly testable (shared-ui
 * runs vitest under `environment: "node"` — there is no jsdom to render a hook
 * in, and RNW is banned by CLAUDE.md architecture invariant 1).
 *
 * Session-scoped by design: the store is in-memory, so every cold open starts at
 * the amount due. For an unguided reviewer that is exactly the right entry state.
 */
import { useMemo } from "react";
import { billCycle, personas } from "@petci/fixtures";
import type { BillCycle } from "@petci/fixtures";
import { useDemoSessionStore } from "./demo-store";
import type { PaymentEntry } from "./demo-store";
import { useNow } from "./use-now";

/**
 * Money is compared in whole cents. Summing floats drifts, and a $0.004
 * remainder would leave a fully-paid bill rendering as unsettled — the exact
 * class of "looks right, is wrong" defect this resolver exists to remove.
 */
function toCents(amount: number): number {
  return Math.round(amount * 100);
}

/**
 * D-2 — the one payment-amount rule, shared by web and mobile so neither can
 * drift: valid ⟺ `0 < amount ≤ remaining`, compared in whole cents.
 *
 * Before this, web capped a custom amount at `remaining × 2` (and at Infinity
 * once the calendar said "paid") while mobile had no upper bound at all, so
 * both surfaces accepted payments against a debt that did not exist — the same
 * defect, twice, with two different shapes.
 *
 * Cents matter at the boundary: paying the exact balance is the commonest
 * action in the flow, and `311.84 <= 311.84` can fail after float arithmetic.
 */
export function isValidPaymentAmount(amount: number, remaining: number): boolean {
  if (!Number.isFinite(amount)) return false;
  const cents = Math.round(amount * 100);
  return cents > 0 && cents <= Math.round(remaining * 100);
}

/**
 * True when a positive amount is above the balance — the case that earns the
 * inline error. Distinct from "not valid": zero and blank are invalid too, but
 * telling someone their empty field exceeds their balance is nonsense.
 */
export function exceedsRemaining(amount: number, remaining: number): boolean {
  if (!Number.isFinite(amount)) return false;
  return Math.round(amount * 100) > Math.round(remaining * 100);
}

export interface BillState {
  /** The current cycle: which month is billed, when it is due, what follows. */
  cycle: BillCycle;
  /**
   * False for a persona with no real bill (the CSR/admin persona, whose
   * `currentBill.month` is `"N/A"`). Consumers suppress due labels, balances
   * and pay CTAs on that branch rather than fabricating a $0 bill.
   */
  hasBill: boolean;
  /** The full amount of the current bill; 0 when `hasBill` is false. */
  total: number;
  /** Sum of this session's payments for this persona. */
  totalPaid: number;
  /** Still owed — `max(0, total − totalPaid)`. The cap for any amount input. */
  remaining: number;
  /** True once this session's payments cover the bill. Never true unpaid. */
  isSettled: boolean;
  /** `YYYY-MM-DD` the settling payment was made; null while unsettled. */
  settledOnIso: string | null;
  /** This session's payments, newest first. */
  payments: PaymentEntry[];
}

export interface BillStateInputs {
  /** The persona's current bill; `undefined` when the persona has none. */
  bill: { month: string; total: number } | undefined;
  /** That persona's session payments, newest first. */
  payments: PaymentEntry[];
  /** "Now" — drives the cycle only, never settled-ness. */
  now: Date;
}

/** Pure core. See {@link useBillState} for the React seam. */
export function resolveBillState({ bill, payments, now }: BillStateInputs): BillState {
  const hasBill = bill !== undefined && bill.month !== "N/A";
  const total = bill !== undefined && bill.month !== "N/A" ? bill.total : 0;

  const totalCents = toCents(total);
  const paidCents = payments.reduce((sum, p) => sum + toCents(p.amount), 0);
  const isSettled = hasBill && paidCents >= totalCents;

  return {
    cycle: billCycle(now),
    hasBill,
    total,
    totalPaid: paidCents / 100,
    remaining: Math.max(0, totalCents - paidCents) / 100,
    isSettled,
    // Newest first, so entry 0 is the payment that crossed the threshold.
    settledOnIso: isSettled ? (payments[0]?.dateIso ?? null) : null,
    payments,
  };
}

/**
 * Resolves the persona's live bill state.
 *
 * @param personaId - the persona to resolve. Callers in a write path pass the
 *   persona captured BEFORE any await, so a mid-flight switch cannot retarget
 *   the read the write is validated against (the E4 stale-closure rule).
 * @param asOf - pins "now" for tests; omitted in app code so the cycle tracks
 *   the shared `useNow()` clock.
 */
export function useBillState(personaId: string, asOf?: Date): BillState {
  const liveNow = useNow();
  const now = asOf ?? liveNow;
  // Stable reference when the persona has paid nothing, so unrelated store
  // writes never re-render consumers.
  const payments = useDemoSessionStore((s) => s.getPaymentEntries(personaId));

  return useMemo(
    () =>
      resolveBillState({
        // Read the live fixture getter rather than a module-eval snapshot, so
        // the total always matches what the screens read from the same persona.
        bill: personas.find((p) => p.id === personaId)?.currentBill,
        payments,
        now,
      }),
    [personaId, payments, now],
  );
}
